import padStart from 'lodash/padStart';
import moment, { Moment } from 'moment-timezone';

import { chargeDataFragment } from 'apollo/queries/autogenerate/operations';
import {
  AutoDebitSettlementStatus,
  BillStatementStatusV2,
  ChargeKind,
  InstallmentPlanKind,
} from 'apollo/queries/autogenerate/schemas';
import { BillStatement } from 'screens/StatementScreen/StatementHistoryScreen';
import { formatMonthDate } from 'shared/utils/date';
import type { AutoDebitDatesByYearMonth } from 'types/misc-info';

export enum UnavailableReasonCode {
  ALREADY_CONVERTED = 'already-converted',
  AMOUNT_TOO_LOW = 'amount.too-low',
  INSTALLMENT_CHARGE = 'installment-charge',
  INSTALLMENT_PLAN_CHARGE = 'installment-plan-charge',
  PAYMENT_BELOW_THRESHOLD = 'payment-below-threshold',
  MERCHANT_DISABLED = 'merchant-disabled',
}

export const is3PayRelated = (charge: chargeDataFragment) =>
  charge.metadata.source.startsWith('isp_');
export const is3PayNegativeCharge = (charge: chargeDataFragment) =>
  charge.kind === ChargeKind.MULTI_PAY_DEBIT &&
  is3PayRelated(charge) &&
  charge.amount < 0;

export const isNPayConvertible = (charge: chargeDataFragment) =>
  !!charge.installmentPlanOptions?.available?.find(
    (option) =>
      option.kind === InstallmentPlanKind.THREE_PAY ||
      option.kind === InstallmentPlanKind.SIX_PAY
  );

export const getFormattedAutoDebitDate = (
  autoDebitDatesByYearMonth: AutoDebitDatesByYearMonth,
  dueDateISOString: string,
  unformatted = false
) => {
  const dueDate = new Date(dueDateISOString);
  const settleYear = dueDate.getFullYear();
  const settleMonth = dueDate.getMonth();
  const mm = padStart((settleMonth + 1).toString(), 2, '0');

  const autoDebitDate = autoDebitDatesByYearMonth[`${settleYear}${mm}`];
  const dd = autoDebitDate ? padStart(`${autoDebitDate}`, 2, '0') : '';

  if (unformatted) {
    return autoDebitDate ? `${settleYear}-${mm}-${dd}` : '';
  }

  return autoDebitDate ? formatMonthDate(`${settleYear}-${mm}-${dd}`) : '';
};

export const formatYen = (
  amount: number,
  unitPos: 'pre' | 'suf' = 'suf',
  unit: 1 | 10000 = 1
) => {
  const roundedAmount = Math.floor(Math.abs(amount));
  const readableAmount = roundedAmount
    .toString()
    .replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  const amountWithUnit =
    unitPos === 'pre'
      ? `¥${readableAmount}`
      : `${readableAmount}${unit === 1 ? '円' : '万円'}`;

  return `${amount < 0 ? '-' : ''}${amountWithUnit}`;
};

export const formatYearMonthDate = (
  date: string | Moment,
  lng: 'en' | 'jp' = 'jp'
) => {
  const timezoneDate = moment(date).tz('Asia/Tokyo');

  if (lng === 'jp') {
    return timezoneDate.format('YYYY年MM月DD日');
  }

  return timezoneDate.format('YYYY/MM/DD');
};

export const formatYearMonth = (
  date: string | Moment,
  lng: 'en' | 'jp' = 'jp'
) => {
  const timezoneDate = moment(date).tz('Asia/Tokyo');

  if (lng === 'jp') {
    return timezoneDate.format('YYYY年M月');
  }

  return timezoneDate.format('YYYY/MM');
};

export const formatYear = (date: string | Moment) => {
  return moment(date).tz('Asia/Tokyo').format('YYYY年');
};

export const getBillingMonth = (period: { start: string; end: string }) =>
  moment(period.end).tz('Asia/Tokyo').get('month') + 1;

export const getBillingYear = (period: { start: string; end: string }) =>
  moment(period.end).tz('Asia/Tokyo').get('year');

export function checkIsBillUsingAutoDebit(bill: BillStatement): boolean {
  const pullStatus = bill.autoDebitSettlement?.pullStatus;

  if (
    pullStatus === AutoDebitSettlementStatus.SUCCESS ||
    pullStatus === AutoDebitSettlementStatus.PROCESSING
  ) {
    return true;
  }

  return false;
}

export function getBillPaymentMethodName(isUsingAutoDebit: boolean): string {
  if (isUsingAutoDebit) {
    return '口座振替'; // Account transfer (meaning auto debit)
  }

  return 'コンビニ払い/銀行振込'; // Convenience store/bank transfer
}

export function getBillDueDateLabel(isUsingAutoDebit: boolean): string {
  if (isUsingAutoDebit) {
    return 'お引き落とし予定日'; // Scheduled withdrawal date
  }

  return 'お支払い期間'; // Payment period
}

export const isPaidBill = (bill: BillStatement) =>
  bill.statusV2 === BillStatementStatusV2.PAID && bill.paidAt;

/**
 * This function rounds off the number to next hundreds, thousands, ten thousands. depending on number of digits.
 * If the number of digits are greater than 5 then it rounds it to next ten thousands.
 * It then returns this rounded off number if the leading non-zero digits are even.
 * If the leading non-zero digits of rounded off number are not even, then it increments it by 1.
 * Eg: 4500 -> 6000, 11111 -> 20000
 *
 */
export function getNumberRoundedToTens(num: number) {
  // round off the number to Integer
  const roundedNum = Math.round(num);
  const roundedNumLength = String(roundedNum).length;
  const numInTens =
    roundedNumLength > 5 ? 10000 : 10 ** (String(roundedNum).length - 1);
  const leadingNonZeroDigits = Math.ceil(roundedNum / numInTens);

  // leading Non Zero Digits of the result should be even.
  return (
    (leadingNonZeroDigits % 2 !== 0
      ? leadingNonZeroDigits + 1
      : leadingNonZeroDigits) * numInTens
  );
}

/**
 * This function first rounds of the number to next hunderds or thousands.
 * It then returns the rounded number and and its half in Japanese.
 *
 */
export function getNumberRoundedToTensInJapanese(num: number) {
  const upperRange = getNumberRoundedToTens(num);

  return {
    upperRangeInJapanese: getNumberInJapanese(upperRange),
    midRangeInJapanese: getNumberInJapanese(upperRange / 2),
  };
}

/**
 * returns number in Japanese.
 * @param - number with first non zero digit followed by zeros
 *
 */
export function getNumberInJapanese(num: number) {
  const lengthOfResult = String(num).length;
  const isValid = /^[1-9]+0+$/.test(String(num));

  if (!isValid) {
    return num;
  }

  if (lengthOfResult === 3) {
    return `${String(num / 100)}百`;
  }

  if (lengthOfResult === 4) {
    return `${String(num / 1000)}千`;
  }

  if (lengthOfResult >= 5) {
    return `${String(num / 10000)}万`;
  }

  return num;
}

export const MONTHLY_USAGE_BAR_MIN_MONTHS = 4;
